/**
 * Portal Leads view (SPEC E4 R3 — reader contract E4-a).
 *
 * Read-only lead list for a CLIENT. Columns render from the tenant's E6
 * field-config for surface `portal_leads`; when the tenant has no config the
 * view degrades to a hardcoded default column set (the degradation contract —
 * exactly today's columns). No edit, no call button.
 */

import { useQuery } from '@tanstack/react-query'
import {
  getPortalLeads,
  getSurfaceFieldConfig,
  resolveColumns,
  type PortalColumn,
  type PortalLead,
} from '@/lib/api/portal'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const SURFACE = 'portal_leads'

// The hardcoded default columns — the degradation fallback when E6 has no
// field-config for this tenant/surface (E4-a).
const DEFAULT_LEAD_COLUMNS: PortalColumn[] = [
  { field_key: 'name', label: 'Name' },
  { field_key: 'phone', label: 'Phone' },
  { field_key: 'status', label: 'Status' },
  { field_key: 'last_call_outcome', label: 'Last Outcome' },
  { field_key: 'next_call_date', label: 'Next Call' },
]

function cellValue(lead: PortalLead, fieldKey: string): string {
  // Standard fields live on the lead; custom fields under custom_fields.
  const direct = (lead as Record<string, unknown>)[fieldKey]
  const custom = lead.custom_fields?.[fieldKey]
  const value = direct ?? custom
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

export function PortalLeads() {
  const leadsQuery = useQuery({
    queryKey: ['portal', 'leads'],
    queryFn: () => getPortalLeads(100),
  })
  const configQuery = useQuery({
    queryKey: ['portal', 'field-config', SURFACE],
    queryFn: () => getSurfaceFieldConfig(SURFACE),
  })

  // Resolve columns: E6 config when present, else the hardcoded default set.
  const columns: PortalColumn[] = configQuery.data
    ? resolveColumns(configQuery.data, DEFAULT_LEAD_COLUMNS)
    : DEFAULT_LEAD_COLUMNS

  if (leadsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading leads…</p>
  }
  if (leadsQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load leads. Please try again.
      </p>
    )
  }

  const leads = leadsQuery.data?.prospects ?? []

  if (leads.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="leads-empty">
        No leads yet.
      </p>
    )
  }

  return (
    <div data-testid="portal-leads">
      <p className="mb-3 text-sm text-muted-foreground">
        {leadsQuery.data?.total ?? leads.length} lead
        {(leadsQuery.data?.total ?? leads.length) === 1 ? '' : 's'}
      </p>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.field_key}>{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.map((lead) => (
              <TableRow key={lead.id}>
                {columns.map((col) => (
                  <TableCell key={col.field_key}>
                    {cellValue(lead, col.field_key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
